import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { FaHistory, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import UnifiedModal from '../UnifiedModal';
import RecentSearchItemMobile from './RecentSearchItemMobile';
import RecentSearchItemDesktop from './RecentSearchItemDesktop';
import { buildResumeQuery, buildStartOverQuery, isSameSearchQuery } from '../../utils/searchQuery';
import { buildSearchData, getStoredSavedOffers } from '../../utils/searchDataUtils';

const RecentSearchesModal = ({ shouldShow, setModalVisible, onResumeSearch, onStartOverSearch, locations = [], currentSearchQuery = '' }) => {
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === 'rtl';

  // Detect if we're on mobile (less than md breakpoint = 768px)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleClose = () => setModalVisible(false);

  // Get user authentication state
  const user = useSelector(state => state.user.user);
  const isLoggedIn = !!user;

  // Use backend-powered recent searches hook
  const {
    searches: recent,
    loading,
    error,
    fetchSearches,
    clearError,
    hasData,
    isEmpty,
    saveSearchWithImmediateUpdate
  } = useRecentSearches({
    autoFetch: false, // Don't auto-fetch, use Redux state
    enableLocalFallback: true // Enable localStorage fallback for non-logged users
  });

  // Force refresh when modal opens (optional - data should already be fresh from Redux)
  useEffect(() => {
    if (shouldShow) {
      // Only fetch if:
      // 1. User is logged in AND
      // 2. We have no data (truly empty, not loading) AND 
      // 3. We're not currently loading
      if (shouldShow && isLoggedIn && recent.length === 0 && !loading) {
        console.log('📡 Fetching recent searches because modal opened with no data...');
        fetchSearches();
      }
    }
  }, [shouldShow, isLoggedIn, recent.length, loading, fetchSearches]);

  // Utility: get flag emoji for residency (dynamic generation for all countries)
  const getResidencyFlag = (residency) => {
    if (!residency || residency.length !== 2) return residency;
    
    // Convert country code to flag emoji using regional indicator symbols
    const codePoints = residency
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0)); // 127397 is the offset for regional indicators
    
    try {
      return String.fromCodePoint(...codePoints);
    } catch (e) {
      // Fallback to country code if emoji generation fails
      return residency;
    }
  };

  // Utility: get location name by code
  const getLocationName = (code) => {
    if (!Array.isArray(locations)) return code;
    const loc = locations.find(l => String(l.locationCode) === String(code));
    return loc ? loc.locationName : code;
  };

  // Utility: format date (backend format includes full timestamp)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle backend format (ISO timestamp) vs local format (dd/MM/yyyy'T'HH:mm)
    if (dateStr.includes('T') && dateStr.includes('-')) {
      // Backend ISO format: 2025-08-03T14:30:00.000Z
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } else if (dateStr.includes('T')) {
      // Local format: dd/MM/yyyy'T'HH:mm
      const [d, t] = dateStr.split('T');
      return `${d} ${t}`;
    }
    return dateStr;
  };

  // Handle search resumption (restores stored filters) and start over (default filters only)
  const handleResumeSearch = (params, filters) => {
    setModalVisible(false);
    onResumeSearch(params, filters);
  };

  const handleStartOverSearch = (params) => {
    // Start over always clears the stored filters, even when the new search returns no results
    saveSearchWithImmediateUpdate(buildSearchData(params, getStoredSavedOffers(recent, params), {}));
    setModalVisible(false);
    onStartOverSearch(params);
  };

  // Render loading state
  const renderLoadingState = () => (
    <div className="text-center py-4">
      <Spinner animation="border" size="sm" className="mx-2" />
      <span>{t('loadingRecentSearches', 'Loading recent searches...')}</span>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className="text-center py-4">
      <div className="text-danger mb-2">
        <FaExclamationTriangle className="mx-2" />
        {error || t('errorLoadingSearches', 'Failed to load recent searches')}
      </div>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => {
          clearError();
          fetchSearches();
        }}
      >
        <FaRedo className="mx-1" />
        {t('retry', 'Retry')}
      </Button>
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div className="text-center py-4 text-muted">
      <FaHistory size={24} className="mb-2 opacity-50" />
      <div>{t('noRecentSearches')}</div>
      {!isLoggedIn && (
        <small className="mt-2 d-block">
          {t('loginToSyncSearches', 'Login to sync your searches across devices')}
        </small>
      )}
    </div>
  );

  const title = (
    <>
      <FaHistory className='mx-2 mb-1'/> 
      {t('recentSearches')}
      {isLoggedIn && hasData && (
        <small className="text-muted mx-2">
          ({recent.length})
        </small>
      )}
    </>
  );

  const footerExtra = isLoggedIn && hasData && !loading && (
    <small className="text-muted mx-auto">
      {t('syncedAcrossDevices', 'Synced across your devices')}
    </small>
  );

  return (
    <UnifiedModal
      show={shouldShow}
      onHide={handleClose}
      title={title}
      dir={rtl ? 'rtl' : 'ltr'}
      footerExtra={footerExtra}
    >
      {loading && renderLoadingState()}
      
      {error && !loading && renderErrorState()}
      
      {isEmpty && !loading && !error && renderEmptyState()}
      
      {hasData && !loading && recent.map((r, idx) => {
        const SearchItemComponent = isMobile ? RecentSearchItemMobile : RecentSearchItemDesktop;
        
        return (
          <SearchItemComponent
            key={r.searchKey || `search-${idx}`}
            search={r}
            idx={idx}
            onResumeSearch={handleResumeSearch}
            onStartOverSearch={handleStartOverSearch}
            getLocationName={getLocationName}
            formatDate={formatDate}
            getResidencyFlag={getResidencyFlag}
            isCurrentSearch={isSameSearchQuery(buildResumeQuery(r.params, r.filters), currentSearchQuery)}
            canStartOver={!isSameSearchQuery(buildStartOverQuery(r.params), currentSearchQuery)}
          />
        );
      })}
    </UnifiedModal>
  );
};

export default RecentSearchesModal;
